'use client';

import { text as sharedText } from '@shared/text';
import {
  getCurrentCoordinates,
  getFallbackCoordinates,
} from '@modules/dashboard/hooks/use_geolocation';
import { useEffect, useState } from 'react';

export interface DateWeatherData {
  dayOfWeek: string;
  day: number;
  month: string;
  year: number;
  season: string;
  seasonColor: string;
  weekNumber: number;
  time: string;
  weather: {
    temperature: number;
    condition: string;
    location: string;
    windSpeed: number;
    humidity: number;
    icon: string;
  } | null;
}

type WeatherData = NonNullable<DateWeatherData['weather']>;

function getSeasonNorthern(date: Date): { name: string; color: string } {
  const month = date.getMonth();
  const day = date.getDate();

  if ((month === 2 && day >= 20) || month === 3 || month === 4 || (month === 5 && day < 21)) {
    let seasonPhase = 'Mid';
    if (month === 2 || (month === 3 && day <= 20)) seasonPhase = 'Early';
    else if (month === 5 || (month === 4 && day >= 20)) seasonPhase = 'Late';
    return { name: `${seasonPhase} Spring`, color: 'text-green-600' };
  }

  if ((month === 5 && day >= 21) || month === 6 || month === 7 || (month === 8 && day < 23)) {
    let seasonPhase = 'Mid';
    if (month === 5 || (month === 6 && day <= 20)) seasonPhase = 'Early';
    else if (month === 8 || (month === 7 && day >= 22)) seasonPhase = 'Late';
    return { name: `${seasonPhase} Summer`, color: 'text-yellow-600' };
  }

  if ((month === 8 && day >= 23) || month === 9 || month === 10 || (month === 11 && day < 21)) {
    let seasonPhase = 'Mid';
    if (month === 8 || (month === 9 && day <= 22)) seasonPhase = 'Early';
    else if (month === 11 || (month === 10 && day >= 21)) seasonPhase = 'Late';
    return { name: `${seasonPhase} Autumn`, color: 'text-orange-600' };
  }

  let seasonPhase = 'Mid';
  if (month === 11 || (month === 0 && day <= 20)) seasonPhase = 'Early';
  else if (month === 2 || (month === 1 && day >= 19)) seasonPhase = 'Late';
  return { name: `${seasonPhase} Winter`, color: 'text-blue-600' };
}

function getSeasonSouthern(date: Date): { name: string; color: string } {
  const month = date.getMonth();
  const day = date.getDate();

  if ((month === 2 && day >= 20) || month === 3 || month === 4 || (month === 5 && day < 21)) {
    let seasonPhase = 'Mid';
    if (month === 2 || (month === 3 && day <= 20)) seasonPhase = 'Early';
    else if (month === 5 || (month === 4 && day >= 20)) seasonPhase = 'Late';
    return { name: `${seasonPhase} Autumn`, color: 'text-orange-600' };
  }

  if ((month === 5 && day >= 21) || month === 6 || month === 7 || (month === 8 && day < 23)) {
    let seasonPhase = 'Mid';
    if (month === 5 || (month === 6 && day <= 20)) seasonPhase = 'Early';
    else if (month === 8 || (month === 7 && day >= 22)) seasonPhase = 'Late';
    return { name: `${seasonPhase} Winter`, color: 'text-blue-600' };
  }

  if ((month === 8 && day >= 23) || month === 9 || month === 10 || (month === 11 && day < 21)) {
    let seasonPhase = 'Mid';
    if (month === 8 || (month === 9 && day <= 22)) seasonPhase = 'Early';
    else if (month === 11 || (month === 10 && day >= 21)) seasonPhase = 'Late';
    return { name: `${seasonPhase} Spring`, color: 'text-green-600' };
  }

  let seasonPhase = 'Mid';
  if (month === 11 || (month === 0 && day <= 20)) seasonPhase = 'Early';
  else if (month === 2 || (month === 1 && day >= 19)) seasonPhase = 'Late';
  return { name: `${seasonPhase} Summer`, color: 'text-yellow-600' };
}

function getWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

function getHemisphereFromTimezone(): boolean {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const southernTimezones = [
      'Australia/',
      'Pacific/Auckland',
      'Pacific/Fiji',
      'Pacific/Tahiti',
      'Antarctica/',
      'Indian/Mauritius',
      'Indian/Reunion',
      'Africa/Johannesburg',
      'Africa/Windhoek',
      'Africa/Maputo',
      'America/Argentina',
      'America/Santiago',
      'America/La_Paz',
      'America/Montevideo',
      'America/Sao_Paulo',
      'America/Lima',
    ];
    return !southernTimezones.some((tz) => timezone.startsWith(tz));
  } catch {
    return true;
  }
}

function getWeatherCondition(code: number): { condition: string; icon: string } {
  if (code === 0) return { condition: 'Clear Sky', icon: 'sun' };
  if (code <= 3) return { condition: 'Partly Cloudy', icon: 'cloud-sun' };
  if (code <= 48) return { condition: 'Foggy', icon: 'cloud' };
  if (code <= 67) return { condition: 'Rainy', icon: 'cloud' };
  if (code <= 77) return { condition: 'Snowy', icon: 'cloud' };
  if (code <= 99) return { condition: 'Stormy', icon: 'cloud' };
  return { condition: 'Cloudy', icon: 'cloud' };
}

function buildDateSnapshot(
  now: Date,
  isNorthernHemisphere: boolean,
  weather: DateWeatherData['weather']
): DateWeatherData {
  const season = isNorthernHemisphere ? getSeasonNorthern(now) : getSeasonSouthern(now);

  let hours = now.getHours();
  const ampm = hours >= 12 ? sharedText.pm : sharedText.am;
  hours = hours % 12;
  hours = hours || 12;

  return {
    dayOfWeek: sharedText.daysOfWeek[now.getDay()],
    day: now.getDate(),
    month: sharedText.months[now.getMonth()],
    year: now.getFullYear(),
    season: season.name,
    seasonColor: season.color,
    weekNumber: getWeekNumber(now),
    time: `${hours}:${now.getMinutes().toString().padStart(2, '0')} ${ampm}`,
    weather,
  };
}

export function useDateWeather(): {
  data: DateWeatherData;
  isLoadingWeather: boolean;
} {
  const [isNorthernHemisphere, setIsNorthernHemisphere] = useState<boolean>(() =>
    getHemisphereFromTimezone()
  );

  const [data, setData] = useState<DateWeatherData>(() =>
    buildDateSnapshot(new Date(), isNorthernHemisphere, null)
  );

  const [isLoadingWeather, setIsLoadingWeather] = useState(true);

  useEffect(() => {
    void getCurrentCoordinates().then((coords) => {
      if (!coords) return;

      const geoBasedHemisphere = coords.latitude >= 0;
      if (geoBasedHemisphere !== isNorthernHemisphere) {
        setIsNorthernHemisphere(geoBasedHemisphere);
        setData(buildDateSnapshot(new Date(), geoBasedHemisphere, null));
      }
    });
  }, [isNorthernHemisphere]);

  useEffect(() => {
    const updateDateTime = () => {
      setData((prev) => ({
        ...buildDateSnapshot(new Date(), isNorthernHemisphere, prev.weather),
      }));
    };

    updateDateTime();
    const interval = setInterval(updateDateTime, 60 * 1000);
    return () => clearInterval(interval);
  }, [isNorthernHemisphere]);

  useEffect(() => {
    const fetchWeatherData = async (latitude: number, longitude: number) => {
      try {
        const response = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m&timezone=auto`
        );

        if (!response.ok) {
          throw new Error('Weather API failed');
        }

        const weatherJson = await response.json();
        const current = weatherJson.current;
        const parsed = getWeatherCondition(current.weather_code);
        const weather: WeatherData = {
          temperature: Math.round(current.temperature_2m),
          condition: parsed.condition,
          location: 'Your Location',
          windSpeed: Math.round(current.wind_speed_10m),
          humidity: current.relative_humidity_2m,
          icon: parsed.icon,
        };

        setData(() => buildDateSnapshot(new Date(), isNorthernHemisphere, weather));
      } finally {
        setIsLoadingWeather(false);
      }
    };

    const fetchWeather = async () => {
      setIsLoadingWeather(true);

      const coords = await getCurrentCoordinates();
      const { latitude, longitude } = coords ?? getFallbackCoordinates();
      await fetchWeatherData(latitude, longitude);
    };

    void fetchWeather();
    const interval = setInterval(() => {
      void fetchWeather();
    }, 30 * 60 * 1000);

    return () => clearInterval(interval);
  }, [isNorthernHemisphere]);

  return { data, isLoadingWeather };
}
